import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { DeesElement, css, customElement, html, property, state } from '@design.estate/dees-element';
import { demoPage, demoStyles } from '../../../demos/templates.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesModal } from '../../00group-overlay/dees-modal/dees-modal.js';
import type { DeesInputText } from '../../00group-input/dees-input-text/dees-input-text.js';
import type { DeesTable } from './dees-table.js';
import type { Column, ICellEditDetail, ITableAction } from './types.js';
import type { TIconKey } from '../../00group-utility/dees-icon/dees-icon.js';

type TAppState = 'Active' | 'Review needed' | 'Paused';
interface IDemoApp {
  id: string;
  name: string;
  domain: string;
  icon: TIconKey;
  owner: string;
  state: TAppState;
  seats: number;
  monthly: number;
  renewal: string;
}

const initialApps = (): IDemoApp[] => [
  { id: 'linear', name: 'Linear', domain: 'Product planning', icon: 'lucide:Layers', owner: 'Product', state: 'Active', seats: 24, monthly: 240, renewal: '2026-10-01' },
  { id: 'figma', name: 'Figma', domain: 'Design & prototyping', icon: 'lucide:Shapes', owner: 'Design', state: 'Review needed', seats: 12, monthly: 180, renewal: '2026-09-15' },
  { id: 'github', name: 'GitHub', domain: 'Code & collaboration', icon: 'lucide:GitBranch', owner: 'Engineering', state: 'Active', seats: 18, monthly: 378, renewal: '2026-10-01' },
  { id: 'notion', name: 'Notion', domain: 'Documents & knowledge', icon: 'lucide:BookOpen', owner: 'Everyone', state: 'Active', seats: 36, monthly: 360, renewal: '2026-10-12' },
  { id: 'slack', name: 'Slack', domain: 'Team communication', icon: 'lucide:MessageSquare', owner: 'Everyone', state: 'Active', seats: 42, monthly: 420, renewal: '2026-10-01' },
  { id: 'vercel', name: 'Vercel', domain: 'Web deployments', icon: 'lucide:Triangle', owner: 'Platform', state: 'Paused', seats: 8, monthly: 160, renewal: '2026-09-28' },
  { id: 'sentry', name: 'Sentry', domain: 'Application monitoring', icon: 'lucide:Activity', owner: 'Engineering', state: 'Review needed', seats: 6, monthly: 120, renewal: '2026-09-20' },
];
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** A rich identity cell, with styles owned by the demo rather than the table. */
@customElement('dees-demo-table-identity')
export class DeesDemoTableIdentity extends DeesElement {
  @property({ attribute: false }) accessor app!: IDemoApp;
  public static styles = [themeDefaultStyles, css`
    :host { display: block; }
    .identity { display: flex; align-items: center; gap: 10px; min-width: 160px; line-height: 1.3; padding: 2px 0; }
    .icon { width: 30px; height: 30px; border-radius: var(--dees-radius-lg); background: var(--dees-color-fill-secondary); display: grid; place-items: center; color: var(--dees-color-text-secondary); flex: 0 0 auto; }
    strong { display: block; font-size: 13px; font-weight: 500; color: var(--dees-color-text-primary); }
    small { display: block; margin-top: 3px; font-size: 11px; color: var(--dees-color-text-muted); white-space: nowrap; }
  `];
  public render() {
    if (!this.app) return html``;
    return html`<div class="identity"><span class="icon"><dees-icon aria-hidden="true" .icon=${this.app.icon} iconSize="16"></dees-icon></span>
      <span><strong>${this.app.name}</strong><small>${this.app.domain}</small></span></div>`;
  }
}

const appColumns: Column<IDemoApp>[] = [
  { key: 'name', header: 'Application', renderer: (_value, row) => html`<dees-demo-table-identity .app=${row}></dees-demo-table-identity>` },
  { key: 'owner', header: 'Team', editable: true, editor: 'dropdown', editorOptions: { options: ['Product', 'Design', 'Engineering', 'Platform', 'Everyone'].map((team) => ({ key: team, label: team })) } },
  { key: 'state', header: 'Access', renderer: (value: TAppState) => html`<dees-badge .type=${value === 'Review needed' ? 'warning' : value === 'Active' ? 'success' : 'default'} .text=${value} .rounded=${true}></dees-badge>` },
  { key: 'seats', header: 'Seats', align: 'end', editor: 'number', parse: (value) => Number(value), validate: (value) => Number.isInteger(value) && value >= 0 || 'Enter a whole number of seats.' },
  { key: 'monthly', header: 'Monthly', align: 'end', renderer: (value: number) => money.format(value), editor: 'number', parse: (value) => Number(value), validate: (value) => Number.isFinite(value) && value >= 0 || 'Enter an amount of zero or more.' },
  { key: 'renewal', header: 'Renews', renderer: (value: string) => date.format(new Date(value)), editor: 'date' },
];

interface IDemoJob { id: string; service: string; region: string; state: string; duration: number; runs: number; }
const jobColumns: Column<IDemoJob>[] = [
  { key: 'service', header: 'Service' }, { key: 'region', header: 'Region' },
  { key: 'state', header: 'Status' },
  { key: 'duration', header: 'Duration (ms)', align: 'end' }, { key: 'runs', header: 'Runs', align: 'end' },
];
const jobs = (): IDemoJob[] => Array.from({ length: 1000 }, (_, i) => ({
  id: `job-${i}`, service: ['Web', 'API', 'Worker', 'Search'][i % 4] + ` ${String(i + 1).padStart(3, '0')}`,
  region: ['Frankfurt', 'London', 'New York'][i % 3], state: i % 17 ? 'Healthy' : 'Review',
  duration: 40 + i % 130, runs: 300 + i * 7,
}));

@customElement('dees-demo-table-workspace')
export class DeesDemoTableWorkspace extends DeesElement {
  @state() accessor apps = initialApps();
  @state() accessor message = 'Select applications to review access. Double-click a seat count, amount, team, or renewal date to edit.';
  @state() accessor reviewOnly = false;
  @state() accessor showFilters = false;
  @state() accessor grid: 'rows' | 'columns' | 'grid' = 'rows';
  @state() accessor advanced = false;
  @state() accessor jobRows = jobs();
  @state() accessor remoteApps = initialApps();
  @state() accessor remoteQuery = '';
  private appCounter = 7;
  private updateNumber = 0;

  public static styles = [themeDefaultStyles, demoStyles, css`
    :host { display: block; min-width: 0; }
    .workspace { display: grid; gap: 16px; min-width: 0; }
    .viewbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
    button { font: inherit; }
    .count { color: var(--dees-color-text-muted); padding-left: 5px; font-variant-numeric: tabular-nums; }
    summary:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px; }
    .feedback { display: flex; align-items: flex-start; gap: 8px; margin: 0; padding: 0 4px; min-height: 32px; color: var(--dees-color-text-secondary); font-size: 12px; line-height: 1.5; }
    .feedback dees-icon { flex: 0 0 auto; margin-top: 2px; }
    .options { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    details { border-top: 1px solid var(--dees-color-border-subtle); padding-top: 4px; }
    summary { list-style: none; display: flex; align-items: center; gap: 8px; padding: 14px 0; cursor: pointer; font-size: 13px; color: var(--dees-color-text-secondary); }
    summary::-webkit-details-marker { display: none; }
    details[open] > summary dees-icon { transform: rotate(90deg); }
    .examples { display: grid; gap: 28px; padding-top: 12px; }
    .example { min-width: 0; }
    .caption { font-size: 12px; color: var(--dees-color-text-secondary); line-height: 1.6; margin: 0 0 12px; }
    #mainTable { --table-max-height: 420px; }
    #jobsTable { --table-max-height: 280px; }
  `];

  private get table() { return this.shadowRoot?.querySelector<DeesTable<IDemoApp>>('#mainTable'); }

  private updateApp(id: string, state: TAppState) {
    const app = this.apps.find((row) => row.id === id);
    if (!app) return;
    this.apps = this.apps.map((row) => row.id === id ? { ...row, state } : row);
    this.message = `${app.name}: ${state.toLowerCase()}.`;
  }

  private async showDetails(app: IDemoApp) {
    await DeesModal.createAndShow({
      heading: app.name, subheading: app.domain, width: 'small',
      content: html`<style>
        .appDetails { display: grid; grid-template-columns: 1fr auto; gap: 16px; margin: 0; font-size: 13px; }
        .appDetails dt { color: var(--dees-color-text-secondary); } .appDetails dd { margin: 0; text-align: right; }
      </style><dl class="appDetails"><dt>Team</dt><dd>${app.owner}</dd><dt>Access</dt><dd>${app.state}</dd>
        <dt>Seats</dt><dd>${app.seats}</dd><dt>Monthly cost</dt><dd>${money.format(app.monthly)}</dd>
        <dt>Renewal</dt><dd>${date.format(new Date(app.renewal))}</dd></dl>`,
      menuOptions: [
        { name: 'Done', action: (modal) => modal!.destroy() },
        ...(app.state === 'Review needed' ? [{ name: 'Approve access', action: async (modal?: DeesModal) => { this.updateApp(app.id, 'Active'); await modal!.destroy(); } }] : []),
      ],
    });
  }

  private async addApplication() {
    const modal = await DeesModal.createAndShow({
      heading: 'Add application', subheading: 'Add an application to this example workspace.', width: 'small',
      content: html`<dees-input-text key="name" label="Application name" placeholder="e.g. Calendar" required></dees-input-text>`,
      menuOptions: [
        { name: 'Cancel', action: (instance) => instance!.destroy() },
        { name: 'Add application', action: async (instance) => {
          const input = instance!.shadowRoot!.querySelector<DeesInputText>('[key="name"]')!;
          const name = String(input.value || '').trim();
          if (!name) { input.validationState = 'invalid'; input.validationText = 'Enter an application name.'; input.focus(); return; }
          this.apps = [...this.apps, { id: `custom-${++this.appCounter}`, name, domain: 'Workspace application', icon: 'lucide:AppWindow', owner: 'Everyone', state: 'Review needed', seats: 1, monthly: 0, renewal: '2026-10-01' }];
          this.reviewOnly = false;
          this.table?.setFilterText('');
          this.message = `Added ${name}. Edit its seats, team, cost, and renewal directly in the table.`;
          await instance!.destroy();
        } },
      ],
    });
    await modal.updateComplete;
  }

  private readonly actions: ITableAction<IDemoApp>[] = [
    { name: 'Add application', iconName: 'lucide:Plus', type: ['header'], actionFunc: async () => this.addApplication() },
    { name: 'View details', iconName: 'lucide:PanelRight', type: ['inRow', 'contextmenu', 'doubleClick'], actionFunc: async ({ item }) => this.showDetails(item) },
    { name: 'Approve access', iconName: 'lucide:CircleCheck', type: ['inRow', 'contextmenu'], actionRelevancyCheckFunc: (app) => app.state === 'Review needed', actionFunc: async ({ item }) => this.updateApp(item.id, 'Active') },
    { name: 'Pause access', iconName: 'lucide:CirclePause', type: ['inRow', 'contextmenu'], actionRelevancyCheckFunc: (app) => app.state === 'Active', actionFunc: async ({ item }) => this.updateApp(item.id, 'Paused') },
    { name: 'Resume access', iconName: 'lucide:Play', type: ['inRow', 'contextmenu'], actionRelevancyCheckFunc: (app) => app.state === 'Paused', actionFunc: async ({ item }) => this.updateApp(item.id, 'Active') },
    { name: 'Copy details', iconName: 'lucide:Copy', type: ['inRow', 'contextmenu'], actionFunc: async ({ item }) => {
      await navigator.clipboard.writeText(`${item.name} · ${item.owner} · ${item.seats} seats · ${money.format(item.monthly)} monthly`);
      this.message = `Copied ${item.name} details.`;
    } },
    { name: 'Edit seats', iconName: 'lucide:Users', type: ['contextmenu'], actionFunc: async ({ item, table }) => table.startEditing(item, appColumns.find((column) => column.key === 'seats')!) },
    { name: 'Approve selected', iconName: 'lucide:CheckCheck', type: ['footer'], actionFunc: async ({ table }) => {
      const selected = this.apps.filter((app) => table.selectedIds.has(app.id));
      if (!selected.length) { this.message = 'Select one or more applications to approve access.'; return; }
      this.apps = this.apps.map((app) => table.selectedIds.has(app.id) ? { ...app, state: 'Active' } : app);
      this.message = `Approved access for ${selected.length} application${selected.length === 1 ? '' : 's'}.`;
    } },
  ];

  private reset() {
    this.apps = initialApps(); this.reviewOnly = false; this.showFilters = false;
    this.table?.setFilterText('');
    if (this.table) { this.table.selectedIds = new Set(); this.table.selectedDataRow = undefined as unknown as IDemoApp; this.table.sortBy = []; }
    this.message = 'Workspace reset. Changes stay in this example.';
  }

  private advanceJobs() {
    this.updateNumber++;
    this.jobRows = this.jobRows.map((job, index) => index < 3
      ? { ...job, duration: 40 + (this.updateNumber * 13 + index * 21) % 130, runs: job.runs + 1 } : job);
  }

  public render() {
    const attention = this.apps.filter((app) => app.state === 'Review needed').length;
    return html`<div class="workspace">
      <div class="viewbar"><dees-input-multitoggle label="Application view" .labelPosition=${'none'}
        .options=${[{key:'all',label:`All applications ${this.apps.length}`},{key:'review',label:`Needs review ${attention}`}]} .selectedOption=${this.reviewOnly ? 'review' : 'all'}
        @change=${(event: CustomEvent) => { this.reviewOnly = event.detail.value === 'review'; }}></dees-input-multitoggle><dees-button type="text" text="Reset workspace" @clicked=${this.reset}></dees-button></div>
      <dees-table id="mainTable" heading1="Applications" heading2="Access, ownership, and renewals across your workspace."
        dataName="applications" .columns=${appColumns} .data=${this.apps} .rowKey=${'id'} .fixedHeight=${true}
        .selectionMode=${'multi'} .showSelectionCheckbox=${true} .dataActions=${this.actions}
        .columnFilters=${this.reviewOnly ? { state: 'Review needed' } : {}} .highlightUpdates=${'flash'}
        @filterChange=${(event: CustomEvent<{ columns: Record<string, string> }>) => {
          this.reviewOnly = event.detail.columns.state === 'Review needed';
        }}
        @cellEdit=${(event: CustomEvent<ICellEditDetail<IDemoApp>>) => {
          this.apps = [...this.apps]; this.message = `Updated ${event.detail.row.name}: ${event.detail.key}.`;
        }}></dees-table>
      <output class="feedback" role="status"><dees-icon aria-hidden="true" icon="lucide:Info" iconSize="14"></dees-icon><span>${this.message}</span></output>
      <details @toggle=${(event: Event) => { this.advanced = (event.target as HTMLDetailsElement).open; }}>
        <summary><dees-icon aria-hidden="true" icon="lucide:ChevronRight" iconSize="14"></dees-icon>Explore table capabilities</summary>
        ${this.advanced ? this.renderExamples() : ''}
      </details>
    </div>`;
  }

  private renderExamples() {
    return html`<div class="examples">
      <section class="example"><div class="options"><dees-input-multitoggle label="Table separators" .labelPosition=${'none'}
        .options=${[{key:'rows',label:'Row separators'},{key:'columns',label:'Column lines'},{key:'grid',label:'Full grid'}]} .selectedOption=${this.grid}
        @change=${(event: CustomEvent) => { this.grid = event.detail.value; }}></dees-input-multitoggle><dees-button type="text" .text=${this.showFilters ? 'Hide column filters' : 'Show column filters'} @clicked=${() => { this.showFilters = !this.showFilters; }}></dees-button></div>
      <p class="caption">Explicit columns keep numbers aligned. Sort multiple columns with Shift-click, or open a header menu to choose sort priority.</p>
      <dees-table heading1="Access review" heading2="Formatting, editing, and multi-column sorting" dataName="applications"
        .columns=${appColumns} .data=${this.apps.slice(0, 4)} .rowKey=${'id'}
        .showGrid=${this.grid === 'grid'} .showVerticalLines=${this.grid === 'columns'}
        .showColumnFilters=${this.showFilters} @cellEdit=${() => { this.apps = [...this.apps]; }}></dees-table></section>
      <section class="example">
        <dees-table heading1="Team directory" heading2="A partial schema, extended by a display function" dataName="people"
          .columns=${[{ key: 'name', header: 'Name' }]} .augmentFromDisplayFunction=${true}
          .displayFunction=${(person: { name: string; email: string; team: string }) => ({ name: person.name, Email: person.email, Team: person.team })}
          .data=${[{ name: 'Alex Morgan', email: 'alex@example.com', team: 'Design' }, { name: 'Sam Rivera', email: 'sam@example.com', team: 'Engineering' }, { name: 'Jamie Chen', email: 'jamie@example.com', team: 'Product' }]}></dees-table>
      </section>
      <section class="example">
        <p class="caption">A thousand rows, a steady selection, and updates limited to the cells that change. Advance the sample feed to see the first three services update.</p>
        <dees-table id="jobsTable" heading1="Service activity" heading2="Virtualized rows with a sticky header" dataName="services"
          .data=${this.jobRows} .columns=${jobColumns} .rowKey=${'id'} .fixedHeight=${true} .virtualized=${true}
          .selectionMode=${'multi'} .showSelectionCheckbox=${true} .highlightUpdates=${'flash'}
          .dataActions=${[{ name: 'Advance updates', iconName: 'lucide:RefreshCw', type: ['header'], actionFunc: async () => this.advanceJobs() } satisfies ITableAction<IDemoJob>]}></dees-table>
      </section>
      <section class="example">
        <p class="caption">Server search leaves result ownership with the application. This example responds locally, with no network calls.</p>
        <dees-table heading1="Application directory" heading2="Search across the remote directory" dataName="applications"
          .searchMode=${'server'} .data=${this.remoteApps} .rowKey=${'id'} .columns=${appColumns.slice(0, 3)}
          @searchRequest=${(event: CustomEvent<{ query: string }>) => {
            this.remoteQuery = event.detail.query;
            this.remoteApps = initialApps().filter((app) => [app.name, app.owner, app.domain].some((text) => text.toLowerCase().includes(this.remoteQuery.toLowerCase())));
          }}></dees-table>
      </section>
      <section class="example"><dees-table heading1="Archived applications" dataName="applications"
        heading2="A clear starting point for an empty collection." .columns=${appColumns.slice(0, 3)} .data=${[]}></dees-table></section>
    </div>`;
  }
}

export const demoFunc = () => demoPage('Tables', 'Review access, make precise edits, and keep a busy workspace organized.',
  html`<dees-demo-table-workspace></dees-demo-table-workspace>`);
